import * as Sentry from '@sentry/browser';

function shouldIgnoreError(error, hint) {
  return isNetworkError(error);
}

const typeErrorFetchFailedValues = new Set([
  'Type error',
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
  'Unhandled Promise Rejection: Failed to fetch',
  'Unhandled Promise Rejection: Load failed',
]);

function isNetworkError(error) {
  const exception = error.exception?.values?.[0];
  const now = Date.now();

  if (exception?.type !== 'TypeError' || !typeErrorFetchFailedValues.has(exception.value)) {
    return false;
  }

  if (!error.breadcrumbs) {
    return false;
  }

  // We go from the back since the last breadcrumb is most likely the erroneous one
  for (let i = error.breadcrumbs.length - 1; i >= 0; i--) {
    const breadcrumb = error.breadcrumbs[i];
    if (!breadcrumb) continue;

    // We only need to check the last 5s of breadcrumbs as any earlier breadcrumbs are definitely unrelated
    if (breadcrumb.timestamp && now - breadcrumb.timestamp * 1000 > 5000) {
      break;
    }

    if (isErroneousBreadcrumb(breadcrumb)) {
      return true;
    }
  }

  return false;
}

function isErroneousBreadcrumb(breadcrumb) {
  if (breadcrumb.level !== 'error' || (breadcrumb.category !== 'xhr' && breadcrumb.category !== 'fetch')) {
    return false;
  }

  const url = breadcrumb.data?.url;
  if (!url) return false;

  return url.startsWith('https://public.fbot.me/');
}

export default () => {
  const { baseHref, environment } = TYROOLA.globals;
  const whitelistUrls = baseHref.replace('https://', '');
  Sentry.init({
    dsn: 'https://39adc0b6d5dc412282bd3bf8a8e33aeb@sentry.io/1272895',
    beforeSend: (error, hint) => {
      const { headers } = error && error.request;

      if (headers && headers['User-Agent'].indexOf('HeadlessChrome') !== -1) {
        console.log('Dropping error event due to headless user-agent');
        return null;
      }
      return shouldIgnoreError(error, hint) ? null : error;
    },
    enabled: environment !== 'development',
    environment,
    debug: environment !== 'production',
    allowUrls: [whitelistUrls, 'static.tyroola.com'],
    denyUrls: [
      // Ignore Google flakiness
      /\/(gtm|ga|analytics)\.js/i,
      /reviews-vertical-.*.js/i,
    ],
    ignoreErrors: [
      'UET is not defined', // this is coming from bing.com UET tracking
      "Can't find variable: UET", // this is coming from bing.com UET tracking
      'xxxxx is not defined', // this is due to wrong setup for facebook tracking pixel in other stores
      "Can't find variable: xxxxx", // same as above
      'Cannot set property "status" of undefined', // coming from NR
      "Can't find variable: Map", // spammy error eating up our quota, Muh is on it
      'Error: Should not already be working.', // https://github.com/facebook/react/issues/17355 , still open
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      'Object captured as promise rejection with keys: abort, always, catch, done, fail',
      'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
      "Can't find variable: _AutofillCallbackHandler",
      'Event `XMLHttpRequestProgressEvent` (type=error) captured as promise rejection',
      'Event `Event` (type=unhandledrejection) captured as promise rejection',
      'Event `ProgressEvent` (type=error) captured as promise rejection',
      'window.cobrowseIOgetRedactedElementsBounds is not a function',
      "Cannot read properties of null (reading 'startGetFields')",
      'The object does not support the operation or argument.',
      'NS_ERROR_NOT_INITIALIZED',
      'No error message',
      'ResizeObserver loop completed with undelivered notifications.',
      'Java object is gone',
      'undefined is not an object (evaluating \'window.webkit.messageHandlers\')',
      'undefined is not an object (evaluating \'window.webkit.messageHandlers[e].postMessage\')',
      'Object captured as promise rejection with keys: message',
    ],
  });
};
